import { z } from 'zod';
import { PlanId, PlanLimitsOverride } from './plans.js';
import { ROLES } from './rbac.js';

/**
 * Platform-admin API (docs/platform-admin.md): cross-company by design, only for a platform admin in an
 * interactive session with a second factor (mutations: signed in within the last hour). Used by the
 * separate admin app (apps/admin). Every mutation is written to `platform_audit_log`.
 */

export const PLATFORM_FRESH_SESSION_MINUTES = 60;
/** Impersonation sessions are read-only and end after this many minutes. */
export const IMPERSONATION_MINUTES = 30;

export const PlatformListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  /** Users: `admins` | `banned` | `unverified` | `no2fa`. Ignored elsewhere. */
  filter: z.enum(['admins', 'banned', 'unverified', 'no2fa']).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type PlatformListQuery = z.infer<typeof PlatformListQuery>;

export const PlatformOverviewDto = z.object({
  users: z.number(),
  usersLast30d: z.number(),
  companies: z.number(),
  accounts: z.object({
    trialing: z.number(),
    paying: z.number(),
    pastDue: z.number(),
    exempt: z.number(),
    pending: z.number(),
  }),
  byPlan: z.record(z.string(), z.number()),
  /** Monthly recurring revenue in cents excl. VAT (yearly prices / 12). Null when prices are unavailable. */
  mrrCents: z.number().nullable(),
  /** Paid this calendar year in cents (KOR threshold watch). */
  revenueYearCents: z.number(),
  /** KOR threshold watch: other turnover under the same VAT number this year (cents, entered by hand) and the limit. */
  kor: z.object({ otherRevenueCents: z.number(), thresholdCents: z.number() }),
  runsPerDay: z.array(z.object({ day: z.string(), runs: z.number(), costUsd: z.number() })),
  health: z.object({
    unsentBillingMail: z.number(),
    failedPayments: z.number(),
    /** Runs not finished yet, across all companies. */
    activeRuns: z.number().optional(),
    failedRuns24h: z.number().optional(),
    /** `running` without progress for 30+ minutes (the reaper should have caught these). */
    stuckRuns: z.number().optional(),
    /** Queued for 10+ minutes: workers may be down or saturated. */
    queuedOver10m: z.number().optional(),
    haltedCompanies: z.number().optional(),
    platformAiTodayUsd: z.number().optional(),
    /**
     * Provider refusals of subscription credentials in the last 24 h, per mode: a spike means Anthropic /
     * OpenAI may be enforcing their terms (docs/plans/2026-10-subscription-compliance-v2.md).
     */
    subscriptionAuthFailures24h: z
      .object({ claudeLogin: z.number(), claudeToken: z.number(), codex: z.number() })
      .optional(),
    /** People who asked to be told when Codex on a ChatGPT plan is available (demand for OpenAI's SIWC). */
    chatgptWaitlist: z.number().optional(),
  }),
});
export type PlatformOverviewDto = z.infer<typeof PlatformOverviewDto>;

export const PlatformUserRow = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  platformAdmin: z.boolean(),
  banned: z.boolean(),
  twoFactorEnabled: z.boolean(),
  companies: z.number(),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
});
export type PlatformUserRow = z.infer<typeof PlatformUserRow>;

export const PlatformUserDto = PlatformUserRow.extend({
  banReason: z.string().nullable(),
  banExpires: z.string().nullable(),
  passkeys: z.number(),
  memberships: z.array(
    z.object({
      orgId: z.string(),
      name: z.string(),
      slug: z.string(),
      role: z.string(),
      accountId: z.string(),
    }),
  ),
  sessions: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      expiresAt: z.string(),
      ip: z.string().nullable(),
      userAgent: z.string().nullable(),
      impersonatedBy: z.string().nullable(),
    }),
  ),
  ownedAccounts: z.array(z.string()),
});
export type PlatformUserDto = z.infer<typeof PlatformUserDto>;

export const PlatformCompanyRow = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  accountId: z.string(),
  plan: z.string().nullable(),
  status: z.string(),
  exempt: z.boolean(),
  members: z.number(),
  agents: z.number(),
  runs30d: z.number(),
  costUsd30d: z.number(),
  createdAt: z.string(),
});
export type PlatformCompanyRow = z.infer<typeof PlatformCompanyRow>;

export const PlatformCompanyDto = PlatformCompanyRow.extend({
  agentsHalted: z.boolean(),
  memberList: z.array(
    z.object({ userId: z.string(), name: z.string(), email: z.string(), role: z.string() }),
  ),
  providers: z.array(
    z.object({ id: z.string(), type: z.string(), authMode: z.string(), enabled: z.boolean() }),
  ),
});
export type PlatformCompanyDto = z.infer<typeof PlatformCompanyDto>;

export const PlatformInvoiceDto = z.object({
  id: z.string(),
  accountId: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  currency: z.string(),
  total: z.number(),
  tax: z.number(),
  amountPaid: z.number(),
  amountRefunded: z.number(),
  hostedInvoiceUrl: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformInvoiceDto = z.infer<typeof PlatformInvoiceDto>;

export const PlatformAccountDto = z.object({
  id: z.string(),
  owner: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
  exempt: z.boolean(),
  plan: z.string().nullable(),
  interval: z.string().nullable(),
  status: z.string(),
  limitsOverride: PlanLimitsOverride.nullable(),
  customerId: z.string().nullable(),
  subscriptionId: z.string().nullable(),
  trialEnd: z.string().nullable(),
  paidThrough: z.string().nullable(),
  periodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  companies: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string() })),
  usage: z.record(z.string(), z.number()),
  limits: z.record(z.string(), z.number().nullable()).nullable(),
  invoices: z.array(PlatformInvoiceDto),
});
export type PlatformAccountDto = z.infer<typeof PlatformAccountDto>;

/** Statistics page: one period (days back from today, UTC). */
export const PlatformStatsQuery = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
  /** One company only (its page); platform-wide sections (growth, Platform AI, top companies) stay empty. */
  orgId: z.string().min(1).max(64).optional(),
});
const Percentiles = z.object({
  p50: z.number().nullable(),
  p90: z.number().nullable(),
  p99: z.number().nullable(),
});
export const PlatformStatsDto = z.object({
  days: z.number(),
  runs: z.object({
    total: z.number(),
    completed: z.number(),
    failed: z.number(),
    cancelled: z.number(),
    perDay: z.array(
      z.object({
        day: z.string(),
        completed: z.number(),
        failed: z.number(),
        cancelled: z.number(),
        other: z.number(),
      }),
    ),
    /** Wall-clock seconds from start to end of completed runs (includes waits for approvals/children). */
    duration: Percentiles.extend({ avg: z.number().nullable() }),
    durationBuckets: z.array(z.object({ label: z.string(), n: z.number() })),
    /** Seconds from created to started. */
    queueWait: Percentiles,
    byRuntime: z.array(
      z.object({ runtime: z.string(), runs: z.number(), failed: z.number(), p50: z.number().nullable() }),
    ),
    topErrors: z.array(z.object({ error: z.string(), n: z.number() })),
    recoveries: z.number(),
    usageLimitParked: z.number(),
  }),
  cost: z.object({
    perDay: z.array(z.object({ day: z.string(), api: z.number(), subscription: z.number() })),
    byModel: z.array(
      z.object({
        provider: z.string(),
        model: z.string(),
        costUsd: z.number(),
        tokens: z.number(),
        billing: z.string(),
      }),
    ),
    totalUsd: z.number(),
    tokens: z.number(),
    /** Share of input tokens served from the prompt cache. */
    cacheHitRatio: z.number().nullable(),
    platformAiUsd: z.number(),
  }),
  usage: z.object({
    activePerDay: z.array(z.object({ day: z.string(), companies: z.number(), users: z.number() })),
    topCompanies: z.array(
      z.object({ id: z.string(), name: z.string(), runs: z.number(), costUsd: z.number() }),
    ),
    sandboxHours: z.number(),
  }),
  growth: z.object({
    signupsPerWeek: z.array(z.object({ week: z.string(), n: z.number() })),
    /** Billing accounts created in the period and how far they got. */
    funnel: z.object({ accounts: z.number(), trial: z.number(), paying: z.number(), exempt: z.number() }),
  }),
});
export type PlatformStatsDto = z.infer<typeof PlatformStatsDto>;

/** Admin → Live: the latest runner sample (anonymous), the last hour, and what the platform is doing now. */
export const PlatformLiveDto = z.object({
  sample: z
    .object({
      at: z.string(),
      ageSeconds: z.number(),
      host: z.object({
        cpus: z.number(),
        cpuPercent: z.number().nullable(),
        load: z.array(z.number()),
        memTotalBytes: z.number(),
        memAvailableBytes: z.number(),
        uptimeSeconds: z.number(),
      }),
      containers: z.object({
        running: z.number(),
        byRole: z.record(z.string(), z.number()),
        sandboxesByOrg: z.array(
          z.object({
            orgId: z.string(),
            name: z.string().nullable(),
            sandboxes: z.number(),
            cpuPercent: z.number(),
            memBytes: z.number(),
            memLimitBytes: z.number(),
          }),
        ),
      }),
    })
    .nullable(),
  history: z.array(
    z.object({
      at: z.string(),
      cpuPercent: z.number().nullable(),
      memUsedPercent: z.number().nullable(),
      containers: z.number(),
      sandboxes: z.number(),
    }),
  ),
  app: z.object({
    runsByStatus: z.record(z.string(), z.number()),
    runningByRuntime: z.array(z.object({ runtime: z.string(), n: z.number() })),
    busyCompanies: z.array(z.object({ id: z.string(), name: z.string(), active: z.number() })),
    oldestQueuedSeconds: z.number().nullable(),
    pendingApprovals: z.number(),
    outboxBacklog: z.number(),
    runsLastHour: z.number(),
    dbSizeBytes: z.number(),
  }),
});
export type PlatformLiveDto = z.infer<typeof PlatformLiveDto>;

// ---- explorer: runs, agents and billing accounts across companies (metadata only, never content)
export const PlatformRunsQuery = z.object({
  /** Run id, agent name or company name. */
  q: z.string().trim().max(200).optional(),
  status: z
    .enum([
      'open',
      'queued',
      'running',
      'scheduled',
      'awaiting_approval',
      'awaiting_human',
      'waiting_children',
      'completed',
      'failed',
      'cancelled',
    ])
    .optional(),
  orgId: z.string().max(64).optional(),
  agentId: z.string().max(64).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const PlatformRunRow = z.object({
  id: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  runtime: z.string().nullable(),
  mode: z.string(),
  status: z.string(),
  error: z.string().nullable(),
  costUsd: z.number(),
  tokens: z.number(),
  steps: z.number(),
  recoveryCount: z.number(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
});
export type PlatformRunRow = z.infer<typeof PlatformRunRow>;
export const PlatformRunDto = PlatformRunRow.extend({
  model: z.string().nullable(),
  parentRunId: z.string().nullable(),
  rootRunId: z.string().nullable(),
  depth: z.number(),
  children: z.number(),
  scheduledReason: z.string().nullable(),
  resumeAt: z.string().nullable(),
  cancelRequested: z.boolean(),
  deployCommit: z.string().nullable(),
  /** Event types over time; payloads (prompts, tool input/output) are never shown here. */
  events: z.array(z.object({ type: z.string(), n: z.number(), first: z.string(), last: z.string() })),
  usage: z.array(
    z.object({
      provider: z.string(),
      model: z.string(),
      billing: z.string(),
      steps: z.number(),
      costUsd: z.number(),
      tokens: z.number(),
    }),
  ),
});
export type PlatformRunDto = z.infer<typeof PlatformRunDto>;
export const PlatformAgentsQuery = z.object({
  q: z.string().trim().max(200).optional(),
  orgId: z.string().max(64).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const PlatformAgentRow = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  roleTitle: z.string(),
  status: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  runtime: z.string().nullable(),
  modelRef: z.string().nullable(),
  version: z.number().nullable(),
  runs30d: z.number(),
  failed30d: z.number(),
  costUsd30d: z.number(),
  lastRunAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformAgentRow = z.infer<typeof PlatformAgentRow>;
export const PlatformAccountsQuery = z.object({
  q: z.string().trim().max(200).optional(),
  filter: z.enum(['exempt', 'trialing', 'paying', 'past_due', 'pending', 'canceled']).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const PlatformAccountRow = z.object({
  id: z.string(),
  ownerEmail: z.string().nullable(),
  plan: z.string().nullable(),
  interval: z.string().nullable(),
  status: z.string(),
  exempt: z.boolean(),
  companies: z.number(),
  trialEnd: z.string().nullable(),
  paidThrough: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformAccountRow = z.infer<typeof PlatformAccountRow>;
/** Admin → System: what is deployed and how it is configured (flags only, never a secret or URL with one). */
export const PlatformSystemDto = z.object({
  build: z.object({ commit: z.string(), node: z.string(), apiUptimeSeconds: z.number() }),
  flags: z.record(z.string(), z.union([z.string(), z.boolean(), z.number(), z.null()])),
  migrations: z.object({ applied: z.number(), lastAppliedAt: z.string().nullable() }),
  queues: z.array(z.object({ name: z.string(), counts: z.record(z.string(), z.number()) })),
  mail: z.object({ unsent: z.number(), sent24h: z.number() }),
  runner: z.object({ lastSampleAt: z.string().nullable() }),
  tables: z.array(z.object({ name: z.string(), rows: z.number(), bytes: z.number() })),
});
export type PlatformSystemDto = z.infer<typeof PlatformSystemDto>;

export const PlatformAuditDto = z.object({
  id: z.number(),
  actorId: z.string(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  after: z.unknown(),
  createdAt: z.string(),
});
export type PlatformAuditDto = z.infer<typeof PlatformAuditDto>;

export const Reason = z.string().trim().min(3).max(500);
export const PlatformCancelRunInput = z.object({ reason: Reason }).strict();
export const PlatformBanInput = z
  .object({ reason: Reason, days: z.number().int().min(1).max(3650).optional() })
  .strict();
export const PlatformReasonInput = z.object({ reason: Reason }).strict();
export const PlatformReset2faInput = z
  .object({ reason: Reason, passkeys: z.boolean().default(false) })
  .strict();
export const PlatformSetPlanInput = z
  .object({
    reason: Reason,
    plan: PlanId.nullable(),
    limitsOverride: PlanLimitsOverride.nullable(),
    exempt: z.boolean(),
  })
  .strict();
export const PlatformSetOwnerInput = z.object({ reason: Reason, userId: z.string().min(1).max(64) }).strict();
export const PlatformExtendTrialInput = z
  .object({ reason: Reason, days: z.number().int().min(1).max(60) })
  .strict();
export const PlatformCancelInput = z.object({ reason: Reason, atPeriodEnd: z.boolean() }).strict();
export const PlatformCreditInput = z
  .object({ reason: Reason, amountCents: z.number().int().min(100).max(1_000_000) })
  .strict();
export const PlatformRefundInput = z
  .object({ reason: Reason, amountCents: z.number().int().positive().optional() })
  .strict();
export const PlatformKorInput = z
  .object({ reason: Reason, otherRevenueCents: z.number().int().min(0).max(100_000_000_00) })
  .strict();
/** Edit a user's profile (support): only the given fields change; a new e-mail must be unused. */
export const PlatformUserProfileInput = z
  .object({
    reason: Reason,
    name: z.string().trim().min(1).max(120).optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    emailVerified: z.boolean().optional(),
  })
  .strict();
export const PlatformSetAdminInput = z.object({ reason: Reason, admin: z.boolean() }).strict();
export const PlatformMemberRoleInput = z.object({ reason: Reason, role: z.enum(ROLES) }).strict();
export const PlatformRenameCompanyInput = z
  .object({ reason: Reason, name: z.string().trim().min(1).max(120) })
  .strict();
/**
 * A new person (support / goodwill): no password, a welcome mail lets them choose one. Optionally their
 * own new company (owner) on a new billing account, exempt = free access.
 */
export const PlatformCreateUserInput = z
  .object({
    reason: Reason,
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().toLowerCase().email().max(254),
    company: z.string().trim().min(1).max(120).nullable().default(null),
    exempt: z.boolean().default(false),
  })
  .strict();
export const PlatformCreatedUserDto = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  accountId: z.string().nullable(),
  mailSent: z.boolean(),
});
export type PlatformCreatedUserDto = z.infer<typeof PlatformCreatedUserDto>;
/** Deleting a person needs their e-mail address typed back. */
export const PlatformDeleteUserInput = z
  .object({ reason: Reason, confirm: z.string().trim().toLowerCase().min(1).max(254) })
  .strict();
/** A new company for an existing person (owner), on a new billing account unless one is given. */
export const PlatformCreateCompanyInput = z
  .object({
    reason: Reason,
    name: z.string().trim().min(1).max(120),
    ownerEmail: z.string().trim().toLowerCase().email().max(254),
    billingAccountId: z.string().min(1).max(64).nullable().default(null),
    exempt: z.boolean().default(false),
  })
  .strict();
/** Add an existing person to a company with a role. */
export const PlatformAddMemberInput = z
  .object({ reason: Reason, email: z.string().trim().toLowerCase().email().max(254), role: z.enum(ROLES) })
  .strict();
/** Deleting a company needs its slug typed back (like the owner's own delete). */
export const PlatformDeleteCompanyInput = z
  .object({ reason: Reason, confirm: z.string().trim().min(1).max(80) })
  .strict();
/** Stripe actions on an open invoice: void it, write it off, or charge the saved card again. */
export const INVOICE_ACTIONS = ['void', 'uncollectible', 'retry'] as const;
export type InvoiceAction = (typeof INVOICE_ACTIONS)[number];
export const PlatformInvoiceActionInput = z
  .object({ reason: Reason, action: z.enum(INVOICE_ACTIONS) })
  .strict();
/** A notice shown at the top of the app for every signed-in user (maintenance, incidents). */
export const ANNOUNCEMENT_TONES = ['info', 'warn'] as const;
export const Announcement = z.object({
  id: z.string().min(1).max(40),
  message: z.string().trim().min(1).max(300),
  tone: z.enum(ANNOUNCEMENT_TONES),
  /** Hidden after this moment (ISO); null = until cleared. */
  until: z.string().datetime().nullable(),
});
export type Announcement = z.infer<typeof Announcement>;
export const PlatformAnnouncementInput = z
  .object({
    reason: Reason,
    message: z.string().trim().max(300),
    tone: z.enum(ANNOUNCEMENT_TONES).default('info'),
    /** Hours until it disappears by itself; omit for "until cleared". Empty message clears it. */
    hours: z
      .number()
      .int()
      .min(1)
      .max(24 * 30)
      .optional(),
  })
  .strict();
/** Dutch small-business scheme (KOR): turnover limit per calendar year, in cents. */
export const KOR_THRESHOLD_CENTS = 20_000_00;
export const PlatformImpersonateDto = z.object({ url: z.string().url(), expiresAt: z.string() });
